import classnames from 'classnames';
import { createUseStyles, useTheme } from 'react-jss';
import { Theme } from '@app/theme';
import { useTranslation } from 'react-i18next';

import {
  QuestionTooltip,
  TextLoadingPlaceholder,
  useBoolModalState,
} from '@app/core';
import {
  BillingMethod,
  CreditBalance,
  Session,
  SessionStatus,
  useBillingInformationsByOrga,
  usePaymentMethod,
  usePlan,
} from '@app/api';
import { ErrorMsg } from '@app/form';

import { PlanBillingModal } from 'components';
import { toEuros } from '@app/i18n';
import { PriceTotals } from 'lib/priceComputation';

const useStyles = createUseStyles((theme: Theme) => ({
  noPaymentMethod: {
    ...theme.fonts.label,
    color: theme.colors.accentAccessible,
    display: 'flex',
    alignItems: 'center',
  },
  icon: {
    marginRight: theme.marginBase,
    fontSize: theme.iconSize.regular,
  },

  container: {
    padding: theme.marginBase * 2,
    backgroundColor: theme.colors.grayBackground,
    borderRadius: theme.borderRadius.std,
    paddingBottom: theme.marginBase,
  },
  line: {
    display: 'flex',
    alignItems: 'center',
    marginBottom: theme.marginBase,
  },
  title: {
    ...theme.fonts.caption,
    flex: 1,
  },
  strong: {
    ...theme.fonts.label,
  },
  methodIcon: {
    color: theme.colors.green,
    fontSize: theme.iconSize.regular,
    marginRight: theme.marginBase,
  },
  summary: {
    ...theme.fonts.caption,
    flex: 1,
  },
  edit: {
    ...theme.fonts.label,
    color: theme.colors.accentAccessible,
    marginLeft: theme.marginBase,
  },
  tooltip: {
    marginLeft: theme.marginBase,
  },
  error: {
    border: {
      width: 1,
      style: 'solid',
      color: theme.colors.red,
    },
  },
}));

interface Props {
  session: Session;
  isAdminBilling?: boolean;
  isBillingInformationRequired?: boolean;
  readonly?: boolean;
  creditBalance?: CreditBalance;
  priceTotals?: PriceTotals;
}

const billingMethodIcons: { [method in BillingMethod]: string } = {
  CREDIT_CARD: 'ri-bank-card-line',
  SEND_INVOICE: 'ri-file-list-3-line',
  ORG_CREDIT: 'ri-coins-line',
};

export const PlanBillingSummary = ({
  session,
  isAdminBilling,
  isBillingInformationRequired,
  readonly,
  creditBalance,
  priceTotals,
}: Props) => {
  const theme: Theme = useTheme();
  const classes = useStyles({ theme });
  const { t } = useTranslation();
  const [opened, open, close] = useBoolModalState(false);
  const organizationId = session.organizationId;

  const { data: billingInformation, isLoading } =
    useBillingInformationsByOrga(organizationId);
  const { data: plan } = usePlan(session.planId);

  const billingMethod = plan?.billingMethod;

  const paymentMethodId = plan?.paymentMethodId;
  const { data: paymentMethod } = usePaymentMethod(paymentMethodId);

  if (isLoading) {
    return (
      <TextLoadingPlaceholder sample={t('billing.paymentMethod.review.add')} />
    );
  }

  const isCreditInsufficient =
    billingMethod === BillingMethod.ORG_CREDIT &&
    (isAdminBilling ? true : session.status === SessionStatus.DRAFT) &&
    (!creditBalance ||
      (!!creditBalance &&
        creditBalance.amount < (priceTotals?.totalNoTax || 0)));

  return (
    <>
      {billingMethod ? (
        <div>
          <div
            className={classnames(classes.container, {
              [classes.error]: isCreditInsufficient,
            })}
          >
            <div className={classes.line}>
              <h5 className={classes.title}>
                <strong className={classes.strong}>
                  {t(`billing.billingMethod.label`)} -{' '}
                </strong>
                {t(`billing.billingMethod.name.${billingMethod}`)}
              </h5>
              <QuestionTooltip
                title={t(`billing.billingMethod.info.${billingMethod}`)}
                className={classes.tooltip}
                variant="gray"
              />
            </div>
            <div className={classes.line}>
              <i
                className={classnames(
                  classes.methodIcon,
                  billingMethodIcons[billingMethod],
                )}
              />
              <div className={classes.summary}>
                {plan.billingMethod === BillingMethod.ORG_CREDIT
                  ? t(`billing.billingMethod.summary.${billingMethod}`, {
                      amount: toEuros(creditBalance?.amount || 0, true),
                    })
                  : t(`billing.billingMethod.summary.${billingMethod}`, {
                      card: paymentMethod,
                      name: billingInformation
                        ? billingInformation.billingOrganization
                        : '',
                      context: paymentMethod ? 'card' : 'no_method',
                    })}
              </div>
              {!readonly && (
                <button className={classes.edit} type="button" onClick={open}>
                  {t('billing.billingMethod.edit')}
                </button>
              )}
            </div>
          </div>
          {isCreditInsufficient && (
            <ErrorMsg
              error={t('billing.billingMethod.info.ORG_CREDIT_INSUFFICIENT')}
            />
          )}
        </div>
      ) : (
        <button
          className={classes.noPaymentMethod}
          type="button"
          onClick={open}
        >
          <i className={classnames('ri-add-line', classes.icon)} />
          {t('billing.paymentMethod.review.add')}
        </button>
      )}
      {isBillingInformationRequired && (
        <ErrorMsg error={t('billing.paymentMethod.review.addRequired')} />
      )}
      <PlanBillingModal
        isOpen={opened}
        onRequestClose={close}
        session={session}
        creditBalance={creditBalance}
        priceTotals={priceTotals}
      />
    </>
  );
};
